/**
 * Shared wallet entry point. Only the selected network's wallet and provider
 * are mounted; switching networks discards the previous screen's private state.
 */

import React from "react";
import BaseApp from "./BaseApp";
import StellarApp from "./stellar-app/StellarApp";
import { ChainProvider } from "./contexts/ChainContext";
import { EVM_CONFIG } from "./config";
import { getSolanaNetwork } from "./config/solana";
import SolanaApp from "./solana-app/SolanaApp";
import {
  WalletNetworkProvider,
  useWalletNetwork,
} from "./networks/WalletNetworkContext";
import type { SettlementNetwork } from "./networks/registry";

/**
 * Main App Wrapper with all providers
 */
function EvmNetworkUnavailable({ network }: { network: SettlementNetwork }) {
  return (
    <div
      style={{
        maxWidth: "720px",
        margin: "48px auto 0",
        padding: "24px 32px",
        background: "#0f172a",
        border: "1px solid #1b263e",
        borderRadius: "16px",
      }}
    >
      <h2 style={{ marginTop: 0, color: "#e8eeff" }}>
        {network.label} is unavailable
      </h2>
      <p style={{ color: "#b0c4de" }}>
        {network.disabledReason
          ? `This deployment does not serve ${network.label}: ${network.disabledReason}`
          : `This deployment does not serve ${network.label}.`}
      </p>
      <a href="/base" style={{ color: "#60a5fa" }}>
        Open the Base wallet
      </a>
    </div>
  );
}

function ActiveWallet() {
  const selection = useWalletNetwork()!;
  const key = `${selection.network.key}:${selection.routeVersion}`;
  // A registry EVM network (DogeOS Chikyu) reached by URL while not
  // configured shows why, instead of a wallet with no pool behind it.
  if (selection.network.kind === "evm" && !selection.network.enabled) {
    return <EvmNetworkUnavailable key={key} network={selection.network} />;
  }
  if (selection.network.kind === "stellar") {
    return <StellarApp key={key} />;
  }
  if (selection.network.kind === "solana") {
    const network = getSolanaNetwork(selection.network.key);
    return network ? <SolanaApp key={key} network={network} /> : null;
  }
  return (
    <ChainProvider
      key={key}
      defaultChainType="evm"
      defaultChainId={EVM_CONFIG.CHAIN_ID}
    >
      <BaseApp />
    </ChainProvider>
  );
}

export default function AppWrapper() {
  return (
    <WalletNetworkProvider>
      <div className="wallet-app">
        <ActiveWallet />
      </div>
    </WalletNetworkProvider>
  );
}
